import { useState } from "react";
import { createPortal } from "react-dom";
import { ShieldCheck, X, IdentificationCard, Handshake, Lock, UserCheck } from "@phosphor-icons/react";

const REASONS = [
  {
    icon: Handshake,
    title: "Vale para as duas partes",
    text: "Locador e locatário passam pela mesma verificação. Quem anuncia sabe quem vai levar o item; quem aluga sabe de quem está levando.",
  },
  {
    icon: UserCheck,
    title: "Bloqueia contas falsas",
    text: "Conferir um documento com foto impede cadastros de fachada, nomes inventados e reincidência de quem já foi bloqueado na plataforma.",
  },
  {
    icon: ShieldCheck,
    title: "Sustenta a caução e as disputas",
    text: "Se algo der errado — atraso, dano ou item não devolvido — só conseguimos mediar e responsabilizar alguém porque a identidade foi confirmada antes.",
  },
  {
    icon: Lock,
    title: "Seus dados ficam guardados",
    text: "O documento é usado só na validação, é visível apenas para a equipe de suporte do AlugaJá e nunca aparece no seu perfil público nem para outros usuários.",
  },
];

/**
 * Link "Por que preciso enviar minha documentação?" + tela explicativa.
 * Usado na etapa de KYC do cadastro (AuthModal) e no card de verificação (EditProfile).
 */
export function KycWhyLink({ className = "", testId = "kyc-why" }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 text-xs font-bold text-[#004E98] underline decoration-dotted underline-offset-2 hover:opacity-80 transition-opacity ${className}`}
        data-testid={`${testId}-link`}
      >
        <ShieldCheck size={15} weight="duotone" />
        Por que preciso enviar minha documentação?
      </button>

      {open && createPortal((
        <div
          className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-end md:items-center md:justify-center"
          onClick={(e) => e.target === e.currentTarget && setOpen(false)}
          data-testid={`${testId}-overlay`}
        >
          <div
            className="bg-white text-[#0F172A] w-full md:max-w-lg md:rounded-2xl rounded-t-2xl max-h-[88vh] flex flex-col pb-safe"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 p-5 border-b border-slate-100">
              <div className="w-11 h-11 rounded-full bg-[#004E98]/10 flex items-center justify-center shrink-0">
                <IdentificationCard size={24} weight="duotone" color="#004E98" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-black text-lg leading-tight">Por que pedimos seu documento</h3>
                <p className="text-xs text-slate-500 mt-0.5">Verificação de identidade AlugaJá</p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1 text-slate-400 hover:text-slate-600"
                data-testid={`${testId}-close`}
              >
                <X size={22} weight="bold" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" style={{ WebkitOverflowScrolling: "touch" }}>
              <p className="text-sm text-slate-700 leading-relaxed">
                No AlugaJá, itens de verdade saem da mão de uma pessoa e vão para a mão de outra.
                Para que isso aconteça com tranquilidade, <b>todo mundo</b> passa pela mesma
                verificação de identidade — sem exceção, dos dois lados do aluguel.
              </p>

              <div className="space-y-3">
                {REASONS.map(({ icon: Icon, title, text }) => (
                  <div key={title} className="flex gap-3">
                    <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                      <Icon size={19} weight="duotone" color="#004E98" />
                    </div>
                    <div>
                      <div className="font-bold text-sm">{title}</div>
                      <p className="text-xs text-slate-600 leading-relaxed mt-0.5">{text}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-[#004E98]/5 border border-[#004E98]/20 rounded-xl p-3.5">
                <p className="text-xs text-slate-700 leading-relaxed">
                  <b>Resumindo:</b> se você for anunciar um item ou alugar o de alguém, pode ter
                  certeza de que a outra pessoa passou exatamente por este mesmo processo. É assim
                  que mantemos a confiança de ponta a ponta.
                </p>
              </div>

              <p className="text-[11px] text-slate-400 leading-relaxed">
                A análise costuma levar até 24h em horário comercial. Tratamos seus dados conforme a
                LGPD e você pode solicitar a exclusão da sua conta e dos seus documentos a qualquer
                momento pelo suporte.
              </p>
            </div>

            <div className="p-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="btn-primary w-full"
                data-testid={`${testId}-ok`}
              >
                Entendi, vamos lá
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
    </>
  );
}

export default KycWhyLink;
